import { z } from 'zod';
import { IssuedStorageObjectKeySchema } from '../storage.js';

/**
 * @aiPurpose per-Request file capacity caps (#1445).
 *   `REQUEST_FILE_MAX_BYTES` mirrors the backend storage-scope ceiling
 *   so the wire layer rejects oversize uploads before issuing a
 *   presigned URL; `REQUEST_FILE_MAX_PER_REQUEST` is the per-Request
 *   aggregate live-row cap enforced at file-bind time.
 */
export const REQUEST_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const REQUEST_FILE_MAX_PER_REQUEST = 20;
export const REQUEST_FILE_NAME_MAX = 256;

/**
 * @aiPurpose allowlist of MIME content types accepted for a Request
 *   file. Covers common images (JPEG/PNG/WebP), PDFs, and Office
 *   documents (DOC/DOCX/XLS/XLSX). Deliberately NARROWER than
 *   `LEAD_FILE_ALLOWED_MIME` — no `image/gif`, no `text/plain`.
 * @aiSecurity load-bearing — the backend mirrors this list when
 *   `scope === 'request-attachment'`. The presigned upload URL also
 *   binds Content-Type so a forged upload is rejected at PUT time as
 *   defense in depth.
 */
export const REQUEST_FILE_ALLOWED_MIME = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
export type RequestFileMime = (typeof REQUEST_FILE_ALLOWED_MIME)[number];
export const RequestFileMimeSchema = z.enum(REQUEST_FILE_ALLOWED_MIME);

const RequestFileNameSchema = z
  .string()
  .min(1)
  .max(REQUEST_FILE_NAME_MAX)
  .refine((s) => s.trim().length > 0, 'fileName must not be whitespace-only')
  // Block path-traversal payloads at the wire layer. The persistence
  // column stores display text only; segments like `..` or `/` should
  // never appear here.
  .refine(
    (s) => !s.includes('/') && !s.includes('\\') && !s.includes('..'),
    'fileName must not contain path separators',
  );

/**
 * @aiPurpose wire projection of a Request file row returned by
 *   `GET /requests/:id/files`.
 * @aiInvariant only live (non-soft-deleted) rows are returned.
 * @aiPii `fileName` is user-supplied display text. The server-side
 *   `objectKey` is INTENTIONALLY absent from this read model — the
 *   download path issues a presigned URL keyed on the file id, so the
 *   raw storage key never leaves the backend.
 */
export const RequestFileSchema = z
  .object({
    id: z.string().uuid(),
    fileName: z.string().min(1).max(REQUEST_FILE_NAME_MAX),
    contentType: z.string(),
    contentLength: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type RequestFile = z.infer<typeof RequestFileSchema>;

/**
 * @aiPurpose request body for POST `/requests/:id/files`. The client
 *   has ALREADY exchanged a presigned upload URL via
 *   `/storage/upload-url` with `scope = 'request-attachment'` and PUT
 *   the bytes; this endpoint records the metadata row that binds the
 *   issued key to the Request.
 * @aiSecurity the service re-validates that:
 *     1. `objectKey` was issued to the caller (the embedded userId
 *        segment equals the JWT subject);
 *     2. `contentType` is in `REQUEST_FILE_ALLOWED_MIME`;
 *     3. `contentLength` ≤ `REQUEST_FILE_MAX_BYTES`;
 *     4. the live-row count for the Request is below
 *        `REQUEST_FILE_MAX_PER_REQUEST`;
 *     5. the `objectKey` is not already bound to another Request.
 */
export const RequestFileCreateInputSchema = z
  .object({
    objectKey: IssuedStorageObjectKeySchema,
    fileName: RequestFileNameSchema,
    contentType: RequestFileMimeSchema,
    contentLength: z.number().int().positive().max(REQUEST_FILE_MAX_BYTES),
  })
  .strict();
export type RequestFileCreateInput = z.infer<typeof RequestFileCreateInputSchema>;
